import React from "react";
import styles from "Styles/Background/backgroundContainer.module.css";
import { motion } from "framer-motion";
import { useVisualContext } from "Contexts/VisualContext";
import classNames from "classnames";
import useWindowSize from "Hooks/useWindowSize";
import NameSuccessBackground from "./NameSuccessBackground";
import CompletePageBackground from "./CompletePageBackground";
import StartPageBackground from "./StartPageBackground";

const getLeftPosition = (viewportWidth) => {
  const min = 450;
  const calculated = parseInt(viewportWidth * 0.4) + 1;

  return `${calculated < min ? min : calculated}px`;
};

const BackgroundNoisyCircle = () => {
  const clientWidth = useWindowSize().width;
  const isMobile = clientWidth < 768;
  const noiseResponsiveSize = clientWidth >= 768 && clientWidth < 1920 ? 750 : 1000;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.2 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        delay: 0.1,
      }}
      className={styles.backgroundNoisyCircleContainer}
    >
      <svg
        width={noiseResponsiveSize}
        height={noiseResponsiveSize}
        viewBox="0 0 1000 1000"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="circle-gradient" spreadMethod="repeat">
            <stop offset="0" stopColor="#1754E7"></stop>
            <stop offset="0.5" stopColor="#2B69FF">
              {!isMobile && (
                <>
                  <animate
                    attributeName="stopColor"
                    values="#1754E7; #2B69FF; #1754E7;"
                    dur="4s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="offset"
                    values="0.5; 1; 0.5; 0; 0.5;"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                </>
              )}
            </stop>
            <stop offset="1" stopColor="#1754E7" />
          </radialGradient>
        </defs>
        <g>
          <circle fill="url(#circle-gradient)" cx="500" cy="500" r="500" />
        </g>
      </svg>
      <div className={styles.backgroundNoisyCircleOverlay}>
        <svg
          width={noiseResponsiveSize}
          height={noiseResponsiveSize}
          viewBox="0 0 1000 1000"
          xmlns="http://www.w3.org/2000/svg"
        >
          <filter id="noiseFilter">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.65"
              numOctaves={isMobile ? 2 : 3}
              stitchTiles="stitch"
            >
              {!isMobile && (
                <>
                  <animate
                    attributeName="baseFrequency"
                    values="0.65; 0.6"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="numOctaves"
                    from="1"
                    to="5"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                </>
              )}
            </feTurbulence>
          </filter>

          <rect width="100%" height="100%" filter="url(#noiseFilter)" />
        </svg>
      </div>
      <div className={styles.backgroundNoisyCircleOverlay2} />
    </motion.div>
  );
};

const Foreground = ({ position = "default" }) => {
  const { width } = useWindowSize();

  const variants = {
    default: {
      width: "40vw",
      left: "0",
    },
    fullwidth: {
      width: "110vw",
      left: "0",
    },
    inverted: {
      left: getLeftPosition(width),
      width: "60vw",
    },
    hidden: {
      width: "0vw",
      left: "0vw",
    },
  };

  const foregroundClasses = classNames(styles.foreground, {
    [`${styles["foregroundInverted"]}`]: position === "inverted",
  });

  return (
    <motion.div
      layout
      className={foregroundClasses}
      initial={false}
      animate={position}
      variants={variants}
      transition={{
        duration: 0.5,
        type: "spring",
      }}
    />
  );
};
const BackgroundContainer = () => {
  const { transitionState, backgroundStyle } = useVisualContext();
  const isFullWidth = backgroundStyle === "fullwidth";
  const isInverted = backgroundStyle === "inverted";

  const bgElements = {
    invertedPage: [
      {
        element: !isInverted && !isFullWidth ? BackgroundNoisyCircle : null,
        key: "noisyCircle",
      },
    ],
  };

  return (
    <>
      <div className={styles.backgroundContainer}></div>
      <Foreground position={backgroundStyle} />
      <div
        className={classNames(styles.backgroundSceneContainer, {
          [styles.backgroundSceneContainerFullwidth]: isFullWidth,
        })}
      >
        {bgElements.invertedPage
          .filter((j) => j.element)
          .map((el, i) =>
            React.createElement(el.element, {
              key: el.key,
              isExiting: transitionState,
            }),
          )}
        <StartPageBackground />
        <CompletePageBackground />
        <NameSuccessBackground />
      </div>
    </>
  );
};

export default BackgroundContainer;
